// ---------------------------------------------------------------------------
// ExpandableTextarea — a Fluent Textarea with an "expand" affordance. A small
// maximize button in the top-right corner opens the shared MaximizeDialog
// containing a large editor bound to the same value, so long content (e.g. a
// system prompt) can be edited comfortably.
//
// When `label` is supplied the inline control is wrapped in a Fluent `Field`
// (label + validation). The maximize dialog is rendered as a SIBLING of that
// Field — never a descendant — so its textarea doesn't inherit the Field's
// generated control id (which would clash with the inline textarea).
// ---------------------------------------------------------------------------

import { Button, Field, Textarea, Tooltip } from '@fluentui/react-components';
import {
  ArrowMaximize20Filled,
  ArrowMaximize20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useState } from 'react';

import { MaximizeDialog } from '../MaximizeDialog/index.js';

import type { ExpandableTextareaProps } from './ExpandableTextarea.interface.js';
import { useStyles } from './ExpandableTextarea.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const MaximizeIcon = bundleIcon(ArrowMaximize20Filled, ArrowMaximize20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

export function ExpandableTextarea({
  value,
  onChange,
  label,
  ariaLabel,
  dialogTitle,
  required,
  hint,
  validationState,
  validationMessage,
  placeholder,
  maxLength,
  rows = 4,
  disabled = false,
  resize = 'vertical',
  expandAriaLabel,
}: ExpandableTextareaProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  const title = dialogTitle ?? label ?? 'Edit';
  const expandLabel =
    expandAriaLabel ?? (label !== undefined ? `Expand ${label}` : 'Expand');

  const control = (
    <div className={styles.wrapper}>
      <Textarea
        className={styles.textareaRoot}
        value={value}
        onChange={(_e, data) => onChange(data.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        resize={resize}
        disabled={disabled}
        aria-label={label === undefined ? ariaLabel : undefined}
        textarea={{ className: styles.inlineTextarea }}
      />
      <Tooltip content="Expand" relationship="label">
        <Button
          appearance="subtle"
          size="small"
          className={styles.expandButton}
          icon={<MaximizeIcon />}
          onClick={() => setOpen(true)}
          aria-label={expandLabel}
        />
      </Tooltip>
    </div>
  );

  return (
    <>
      {label !== undefined ? (
        <Field
          label={label}
          required={required}
          hint={hint}
          validationState={validationState}
          validationMessage={validationMessage}
        >
          {control}
        </Field>
      ) : (
        control
      )}

      <MaximizeDialog open={open} onOpenChange={setOpen} title={title}>
        <Textarea
          className={styles.dialogTextareaRoot}
          value={value}
          onChange={(_e, data) => onChange(data.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          resize="none"
          aria-label={title}
          // In read-only mode the dialog stays viewable/scrollable/selectable
          // (readOnly) rather than fully disabled, so users can read long text.
          textarea={{ className: styles.dialogTextarea, readOnly: disabled }}
        />
      </MaximizeDialog>
    </>
  );
}
